import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import usePageMeta from '../lib/usePageMeta';
import PageHero from '../components/PageHero';
import ProjectCard from '../components/ProjectCard';
import { SectionHead, TechMarquee } from '../components/Blocks';
import { PROJECTS, PROJECT_FILTERS, STATS } from '../data';
import { refreshScroll } from '../lib/useScrollAnimations';

export default function Portfolio() {
  usePageMeta('Portfolio', 'Case studies from Origo Private Limited: SaaS dashboards, mobile apps, e-commerce stores, brand identities and animated videos.');
  const [filter, setFilter] = useState('All');
  const gridRef = useRef(null);
  const first = useRef(true);
  const items = PROJECTS.filter((p) => filter === 'All' || p.cat === filter);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const cards = gridRef.current?.children;
    if (cards && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.fromTo(cards, { autoAlpha: 0, y: 40, scale: 0.94 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out', clearProps: 'transform' });
    }
    refreshScroll(100);
  }, [filter]);

  return (
    <>
      <PageHero
        eyebrow="Our work"
        title="Projects that delivered real results"
        description="A selection of websites, apps, brands and animations we have built with clients across industries. Open any project to read the full case study."
        crumbs={[{ label: 'Portfolio' }]}
      />

      <section className="section bg-brand-soft">
        <div className="container-x">
          <div className="flex flex-wrap justify-center gap-2" data-anim="up" role="tablist" aria-label="Filter projects">
            {PROJECT_FILTERS.map((f) => (
              <button key={f} role="tab" aria-selected={filter === f} onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${filter === f ? 'bg-brand text-white shadow-lg' : 'bg-white text-brand hover:bg-brand/10'}`}>
                {f}
              </button>
            ))}
          </div>
          <div ref={gridRef} className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHead eyebrow="By the numbers" title="A track record you can rely on" />
          <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-3xl bg-brand/15 lg:grid-cols-4" data-anim="up">
            {STATS.map((s) => (
              <div key={s.label} className="bg-brand-soft px-6 py-9 text-center">
                <p className="font-display text-4xl font-bold text-brand sm:text-5xl" data-count={s.value} data-suffix={s.suffix}>0</p>
                <p className="mt-2 text-sm font-medium text-slate-600">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-20"><TechMarquee /></div>
        </div>
      </section>
    </>
  );
}
